// Deterministic mocks for the admin scraper API. Real backend lands via
// DEV-214; until it is on the integration trunk the dashboard exercises the
// same components against this fixture set so visual diffs and unit tests
// remain stable.
//
// The fixture is intentionally varied across the four canonical portals so
// every critical state from plan §7 has a representative card on first paint:
//   BoligPortal     → healthy (most recent done, zero errors)
//   Lejebolig       → degraded (done with errors, retry-eligible)
//   Lejeboligportal → running  (mid-pipeline, items done/total visible)
//   BoligZonen      → failed   (failed at scrape, retry inline CTA)
//
// Edit deliberately — the dashboard tests pin behaviour on these slugs.

import type {
  PaginatedRuns,
  PipelineHealthResponse,
  RunsQuery,
  ScrapeRunDetail,
  ScrapeRunSummary,
  SignalFeedResponse
} from './adminScraper';

// ---------- Time helpers ----------

const NOW = () => new Date();
function mins(n: number): string {
  return new Date(NOW().getTime() - n * 60_000).toISOString();
}
function hrs(n: number): string {
  return new Date(NOW().getTime() - n * 60 * 60_000).toISOString();
}
function days(n: number): string {
  return new Date(NOW().getTime() - n * 24 * 60 * 60_000).toISOString();
}

// ---------- Pipeline health ----------

export async function fetchPipelineHealth(): Promise<PipelineHealthResponse> {
  return {
    portals: [
      {
        slug: 'boligportal',
        name: 'BoligPortal',
        headline: 'BoligPortal: completed (12m ago) — 1284 active listings',
        last_run_status: 'completed',
        last_run_at: mins(12),
        active_listings: 1284,
        error_count_last_run: 0
      },
      {
        slug: 'lejebolig',
        name: 'Lejebolig',
        headline: 'Lejebolig: done with errors (4h ago) — 873 active listings — 47 errors',
        last_run_status: 'completed',
        last_run_at: hrs(4),
        active_listings: 873,
        error_count_last_run: 47
      },
      {
        slug: 'lejeboligportal',
        name: 'Lejeboligportal',
        headline: 'Lejeboligportal: running — 612 active listings',
        last_run_status: 'running',
        last_run_at: mins(3),
        active_listings: 612,
        error_count_last_run: 0
      },
      {
        slug: 'boligzonen',
        name: 'BoligZonen',
        headline: 'BoligZonen: failed (last night at scrape stage) — 401 active listings',
        last_run_status: 'failed',
        last_run_at: hrs(11),
        active_listings: 401,
        error_count_last_run: 12
      }
    ]
  };
}

// ---------- Runs ----------

const baseRuns: ScrapeRunSummary[] = [
  {
    id: 'run-bp-1',
    portal_slug: 'boligportal',
    status: 'completed',
    trigger: 'scheduled',
    current_stage: 'enrich',
    target_count: 1300,
    error_count: 0,
    started_at: mins(34),
    completed_at: mins(12),
    created_at: mins(34)
  },
  {
    id: 'run-lb-1',
    portal_slug: 'lejebolig',
    status: 'completed',
    trigger: 'scheduled',
    current_stage: 'enrich',
    target_count: 900,
    error_count: 47,
    started_at: hrs(5),
    completed_at: hrs(4),
    created_at: hrs(5)
  },
  {
    id: 'run-lp-1',
    portal_slug: 'lejeboligportal',
    status: 'running',
    trigger: 'manual',
    current_stage: 'diff',
    target_count: 800,
    error_count: 0,
    started_at: mins(3),
    completed_at: null,
    created_at: mins(3)
  },
  {
    id: 'run-bz-1',
    portal_slug: 'boligzonen',
    status: 'failed',
    trigger: 'scheduled',
    current_stage: 'scrape',
    target_count: 500,
    error_count: 12,
    started_at: hrs(11),
    completed_at: hrs(11),
    created_at: hrs(11)
  },
  {
    id: 'run-bp-2',
    portal_slug: 'boligportal',
    status: 'completed',
    trigger: 'scheduled',
    current_stage: 'enrich',
    target_count: 1300,
    error_count: 0,
    started_at: hrs(26),
    completed_at: hrs(25),
    created_at: hrs(26)
  },
  {
    id: 'run-lb-2',
    portal_slug: 'lejebolig',
    status: 'completed',
    trigger: 'scheduled',
    current_stage: 'enrich',
    target_count: 900,
    error_count: 0,
    started_at: hrs(28),
    completed_at: hrs(27),
    created_at: hrs(28)
  },
  {
    id: 'run-lp-2',
    portal_slug: 'lejeboligportal',
    status: 'completed',
    trigger: 'scheduled',
    current_stage: 'enrich',
    target_count: 800,
    error_count: 3,
    started_at: hrs(30),
    completed_at: hrs(29),
    created_at: hrs(30)
  },
  {
    id: 'run-bz-2',
    portal_slug: 'boligzonen',
    status: 'completed',
    trigger: 'scheduled',
    current_stage: 'enrich',
    target_count: 500,
    error_count: 0,
    started_at: hrs(35),
    completed_at: hrs(34),
    created_at: hrs(35)
  },
  // A couple of older runs to validate the 5d/30d toggle.
  {
    id: 'run-bp-old-1',
    portal_slug: 'boligportal',
    status: 'completed',
    trigger: 'scheduled',
    current_stage: 'enrich',
    target_count: 1300,
    error_count: 1,
    started_at: days(8),
    completed_at: days(8),
    created_at: days(8)
  },
  {
    id: 'run-lb-old-1',
    portal_slug: 'lejebolig',
    status: 'cancelled',
    trigger: 'manual',
    current_stage: 'diff',
    target_count: 900,
    error_count: 0,
    started_at: days(12),
    completed_at: days(12),
    created_at: days(12)
  }
];

export async function fetchRuns(query: RunsQuery = {}): Promise<PaginatedRuns> {
  const days_ = query.days ?? 5;
  const cutoff = NOW().getTime() - days_ * 24 * 60 * 60_000;
  let items = baseRuns.filter((r) => new Date(r.created_at).getTime() >= cutoff);
  if (query.status?.length) items = items.filter((r) => query.status?.includes(r.status));
  if (query.portal) items = items.filter((r) => r.portal_slug === query.portal);
  // Newest first — matches backend ordering.
  items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const page = query.page ?? 1;
  const pageSize = query.page_size ?? 25;
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    total: items.length,
    page,
    page_size: pageSize
  };
}

export async function fetchRunDetail(runId: string): Promise<ScrapeRunDetail | null> {
  const summary = baseRuns.find((r) => r.id === runId);
  if (!summary) return null;

  // Per-stage counters keyed exactly to the field names the run-detail page
  // looks for. Stages that haven't produced numbers are absent so the panel
  // can mark them pending instead of "0".
  const stageProgressFull: Record<string, Record<string, number>> = {
    scrape: { pages_fetched: 14, listings_found: summary.target_count ?? 0, dupes_skipped: 8 },
    diff: { new: 21, price_changes: 9, delistings: 3, relistings: 1, unchanged: 578 },
    persist: { saved: Math.max(0, (summary.target_count ?? 0) - summary.error_count) },
    enrich: {
      address_resolved: 542,
      linked_to_unit: 511,
      bbr_disagreements: 7,
      enrich_failed: Math.max(0, summary.error_count - 12)
    }
  };

  let stage_progress: Record<string, Record<string, number>> | null = null;
  if (summary.status === 'running') {
    stage_progress = { scrape: stageProgressFull.scrape, diff: stageProgressFull.diff };
  } else if (summary.status === 'failed') {
    stage_progress = { [summary.current_stage ?? 'scrape']: { pages_fetched: 3, listings_found: 0 } };
  } else if (summary.status === 'completed') {
    stage_progress = stageProgressFull;
  }

  // Grouped error log — backend writes one entry per failure with stage +
  // message. The detail page groups by message so the dominant failure mode
  // floats to the top. The fixtures below exercise the grouping UI.
  let error_log: Array<Record<string, unknown>> | null = null;
  if (summary.error_count > 0) {
    if (summary.id === 'run-bz-1') {
      error_log = [
        ...Array.from({ length: 9 }, (_, i) => ({
          stage: 'scrape',
          message: 'request timed out after 30s',
          listing_url: `https://boligzonen.dk/listing/${i + 1}`,
          ts: hrs(11)
        })),
        { stage: 'scrape', message: '503 service unavailable', listing_url: 'https://boligzonen.dk/listing/42', ts: hrs(11) },
        { stage: 'scrape', message: '503 service unavailable', listing_url: 'https://boligzonen.dk/listing/43', ts: hrs(11) },
        { stage: 'normalize', message: 'unparseable rent: "ring for pris"', listing_url: 'https://boligzonen.dk/listing/77', ts: hrs(11) }
      ];
    } else {
      error_log = [
        ...Array.from({ length: 30 }, (_, i) => ({
          stage: 'enrich',
          message: 'BBR address not found',
          listing_url: `https://lejebolig.dk/listing/${i + 1}`,
          ts: hrs(4)
        })),
        ...Array.from({ length: 12 }, (_, i) => ({
          stage: 'enrich',
          message: 'DAR enrichment timeout',
          listing_url: `https://lejebolig.dk/listing/${100 + i}`,
          ts: hrs(4)
        })),
        ...Array.from({ length: 5 }, (_, i) => ({
          stage: 'normalize',
          message: 'unparseable size: "stort"',
          listing_url: `https://lejebolig.dk/listing/${200 + i}`,
          ts: hrs(4)
        }))
      ];
    }
  }

  return {
    ...summary,
    stage_progress,
    stage_checkpoints: null,
    error_log,
    pipeline_result: null,
    paused_at: null,
    updated_at: summary.completed_at ?? summary.started_at ?? summary.created_at
  };
}

export async function startRun(
  portalSlug: string,
  _targetCount?: number
): Promise<{ id: string; status: string }> {
  return { id: `run-${portalSlug}-${Date.now()}`, status: 'pending' };
}

export async function retryRun(
  runId: string,
  stage?: string
): Promise<{ status: string; retry_from_stage?: string }> {
  const summary = baseRuns.find((r) => r.id === runId);
  return { status: 'pending', retry_from_stage: stage ?? summary?.current_stage ?? 'scrape' };
}

export async function cancelRun(_runId: string): Promise<{ status: string }> {
  return { status: 'cancelled' };
}

// ---------- Signals (divergence panel) ----------

export async function fetchDivergenceSignals(limit = 10): Promise<SignalFeedResponse> {
  return {
    items: [
      {
        id: 'sig-1',
        eventType: 'cross_site_price_divergence',
        createdAt: mins(8),
        unit: {
          id: 'unit-1234',
          address: 'Vesterbrogade 92, 4. tv',
          city: 'København V',
          postnummer: '1620'
        },
        listing: null,
        payload: {
          portals: [
            { portal: 'BoligPortal', rent: 14_500 },
            { portal: 'Lejebolig', rent: 12_900 }
          ],
          spread_pct: 12.4
        },
        summary: 'Cross-site price divergence — 12.4% spread'
      },
      {
        id: 'sig-2',
        eventType: 'cross_site_price_divergence',
        createdAt: mins(22),
        unit: {
          id: 'unit-3456',
          address: 'Strandvejen 14, 2. th',
          city: 'København Ø',
          postnummer: '2100'
        },
        listing: null,
        payload: {
          portals: [
            { portal: 'BoligPortal', rent: 18_200 },
            { portal: 'Lejeboligportal', rent: 16_700 }
          ],
          spread_pct: 9.0
        },
        summary: 'Cross-site price divergence — 9.0% spread'
      },
      {
        id: 'sig-3',
        eventType: 'cross_site_new_listing',
        createdAt: mins(45),
        unit: {
          id: 'unit-9876',
          address: 'Mejlgade 4, 1.',
          city: 'Aarhus C',
          postnummer: '8000'
        },
        listing: { id: 'listing-9876', url: 'https://example.com', portal: 'BoligZonen' },
        payload: { spread_pct: 6.1 },
        summary: 'New listing on BoligZonen for an existing unit'
      }
    ].slice(0, limit),
    nextCursor: null,
    hasMore: false
  };
}
